/**
 * TIP Shell — the SEAL. The sealed iframe's attributes are set in this ONE audited
 * place so the §6a posture (spike-validated) can never drift. sandbox="allow-scripts"
 * ONLY (no allow-same-origin ⇒ opaque origin), no referrer, empty Permissions Policy.
 *
 * The frame's CSP (default-src 'none' / connect-src 'none' / navigate-to 'none' / …,
 * SPIKE-SPEC §6b) is enforced by the SANDBOX ORIGIN server as an HTTP response header —
 * NOT settable from the parent — so the Shell wires the CSP posture by (a) always
 * loading the frame from the distinct opaque SANDBOX_ORIGIN and (b) passing the
 * COMPUTED tier into the frame URL so the sandbox serves the tier-appropriate policy.
 */
import type { Tier } from './protocol.js';

/**
 * Opaque sandboxed origin ⇒ an outbound `window.postMessage` to the frame can only
 * be targeted with `'*'` (an opaque origin never equals a URL origin). Spike-proven.
 * After the port handshake we send via the MessagePort (origin-agnostic), so this is
 * used only for any window-level fallback send.
 */
export const FRAME_TARGET = '*' as const;

export interface SealOptions {
  sandboxOrigin: string; // the distinct opaque origin (apps.tip.run / local :8081)
  framePath: string; // e.g. '/app.html' — the doc the sandbox serves
  tier: Tier; // COMPUTED tier (never app-declared) — drives the sandbox's CSP choice
  nonce: string; // per-launch handshake nonce
  appId: string;
  nav?: string | null; // adversarial nav-vector selector (test parity with the spike)
  /** Signed CSP-report attribution token from /gate/mount (absent in dev without SSO). */
  cspReportToken?: string | null;
  /** Signed app-source token from /gate/mount. Only a GENERATED app needs it: the
   *  sandbox presents it to tip-server to fetch that app's published source.
   *  DOMAIN-SEPARATED from the report token — never pass one where the other belongs. */
  appSourceToken?: string | null;
}

/**
 * Build the sealed iframe. §6a attributes are literal and exhaustive: any attribute
 * not set here is intentionally absent.
 */
export function createSealedFrame(opts: SealOptions): HTMLIFrameElement {
  const q = new URLSearchParams();
  q.set('tier', opts.tier);
  q.set('launch', opts.nonce); // param name is 'launch' (the sandbox also tolerates ?nonce=)
  q.set('app', opts.appId);
  if (opts.nav) q.set('nav', opts.nav);
  // Attributed CSP reporting: the sandbox embeds this into the frame doc's report-uri
  // (charset-validated there). Absent ⇒ the frame keeps the same-origin-only sink.
  if (opts.cspReportToken) q.set('rpt', opts.cspReportToken);
  // Generated apps only: the sandbox exchanges this for the app's source. A built-in
  // app ignores it (its bundle is on disk).
  if (opts.appSourceToken) q.set('src', opts.appSourceToken);
  const src = `${opts.sandboxOrigin}${opts.framePath}?${q.toString()}`;

  const iframe = document.createElement('iframe');
  iframe.id = 'sealed';
  iframe.title = 'Classroom activity';
  // §6a — verbatim. allow-scripts ONLY (no allow-same-origin, no allow-popups,
  // no allow-forms, no allow-top-navigation). This is the seal.
  iframe.setAttribute('sandbox', 'allow-scripts');
  iframe.setAttribute('referrerpolicy', 'no-referrer');
  iframe.setAttribute('allow', ''); // empty Permissions Policy
  iframe.setAttribute('src', src);
  return iframe;
}
